import CloseIcon from "@mui/icons-material/Close";
import { Alert, IconButton } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import * as React from "react";

import { RobotoTypography } from "@/shared/components";
import { VisibilityToggle } from "@/shared/components/VisibilityToggle";
import { getGeoMessagePaths } from "@/shared/components/visualization/Panel/MapPanel/geoTypes";
import {
  CanonicalDataType,
  getCorrespondingGeoType,
  isGeoSpecificType,
  isLatitudeType,
  isLongitudeType,
  isMessagePathAttr,
  MessagePathNode,
  PathParser,
} from "@/shared/domain/topics";
import { actions, useVizDispatch } from "@/shared/state/visualization";
import {
  CoordinateUnit,
  MapPath,
  TopicData,
} from "@/shared/state/visualization/schema/v1";

import { DataSourceSelector } from "../../controls/DataSourceSelector";

import { CoordinateUnitSelect } from "./CoordinateUnitSelect";
import styles from "./Paths.module.css";

interface PathsProps {
  panelId: string;
  paths: MapPath[];
}

export function Paths({ panelId, paths }: PathsProps) {
  // Construct a cache of the path ids for quick lookup when checking for duplicates
  const pathCache = React.useMemo(() => {
    return new Map(
      paths.map((path) => {
        const { latTopicData, lonTopicData } = getGeoMessagePaths(path);
        const latId = latTopicData.messagePath.id;
        const lonId = lonTopicData.messagePath.id;
        return [`lat:${latId}, lon:${lonId}`, path.id];
      }),
    );
  }, [paths]);

  const isDuplicatePath = React.useCallback(
    (currentPath: MapPath) => (nextTopicData: TopicData[]) => {
      const id = `lat:${nextTopicData[0].messagePath.id}, lon:${nextTopicData[1].messagePath.id}`;
      return pathCache.has(id) && pathCache.get(id) !== currentPath.id;
    },
    [pathCache],
  );

  if (paths.length === 0) {
    return null;
  }

  return (
    <div className={styles.paths}>
      {paths.map((path, i) => {
        return (
          <Path
            key={path.id}
            path={path}
            panelId={panelId}
            pathname={`Path ${i + 1}`}
            isDuplicatePath={isDuplicatePath(path)}
          />
        );
      })}
    </div>
  );
}

interface PathProps {
  panelId: string;
  path: MapPath;
  pathname: string;
  isDuplicatePath: (nextTopicData: TopicData[]) => boolean;
}

function Path({ panelId, path, pathname, isDuplicatePath }: PathProps) {
  return (
    <div className={styles.path}>
      <div className={styles.pathHeader}>
        <RobotoTypography variant="overline">{pathname}</RobotoTypography>
        <PathControls panelId={panelId} path={path} />
      </div>
      <PathData
        panelId={panelId}
        path={path}
        isDuplicatePath={isDuplicatePath}
      />
    </div>
  );
}

interface PathControlsProps {
  panelId: string;
  path: MapPath;
}

function PathControls({ panelId, path }: PathControlsProps) {
  const dispatch = useVizDispatch();

  const onRemovePath = (path: MapPath) => {
    dispatch(actions.removePathFromMapPanel(panelId, path.id));
  };
  const onShowPath = (path: MapPath) => {
    dispatch(actions.setPathVisibility(panelId, path.id, !path.visible));
  };

  return (
    <div>
      <VisibilityToggle
        className={styles.iconButton}
        on={path.visible}
        onToggle={() => onShowPath(path)}
      />
      <IconButton
        aria-label="remove"
        className={styles.iconButton}
        onClick={() => onRemovePath(path)}
        title="Remove Path"
      >
        <CloseIcon fontSize="inherit" />
      </IconButton>
    </div>
  );
}

interface GeoCoordinate {
  topicData: TopicData;
  canonicalDataType: CanonicalDataType;
}

interface GeoPair {
  latitude: GeoCoordinate | null;
  longitude: GeoCoordinate | null;
}

function geoCoordinateFromTopicData(
  topicData: TopicData,
): GeoCoordinate | null {
  const lastAttr = [...topicData.messagePath.parts]
    .reverse()
    .find(isMessagePathAttr);
  if (!lastAttr) {
    return null;
  }
  return {
    topicData,
    canonicalDataType: lastAttr.dataType,
  };
}

interface PathDataProps {
  panelId: string;
  path: MapPath;
  isDuplicatePath: (nextTopicData: TopicData[]) => boolean;
}

function PathData({ panelId, path, isDuplicatePath }: PathDataProps) {
  // The lat/lon selectors are independent uncontrolled form fields, but their
  // option filters depend on each other's selection. We track the coordinate
  // pair here so we can constrain the partner selector's options (e.g., if
  // longitude is from topic X, only show latitudes from topic X) and validate
  // the pair as a single entity before committing to global state.

  const dispatch = useVizDispatch();
  const [error, setError] = React.useState<string | undefined>(undefined);

  const { latTopicData: latitudeTopicData, lonTopicData: longitudeTopicData } =
    getGeoMessagePaths(path);

  const latitude = {
    fileId: latitudeTopicData.topic.association.association_id,
    path: [
      latitudeTopicData.topic.name,
      MessagePathNode.partsToString(latitudeTopicData.messagePath.parts),
    ].join(PathParser.TOPIC_SEPARATOR),
  };

  const longitude = {
    fileId: longitudeTopicData.topic.association.association_id,
    path: [
      longitudeTopicData.topic.name,
      MessagePathNode.partsToString(longitudeTopicData.messagePath.parts),
    ].join(PathParser.TOPIC_SEPARATOR),
  };

  const [pair, setPair] = React.useState<GeoPair>(() => {
    const latitude = geoCoordinateFromTopicData(latitudeTopicData);
    const longitude = geoCoordinateFromTopicData(longitudeTopicData);
    return { latitude, longitude };
  });

  const commitPair = React.useCallback(
    (nextPair: GeoPair) => {
      if (!nextPair.latitude || !nextPair.longitude) {
        // Don't commit incomplete pairs
        return;
      }

      const nextTopicData: TopicData[] = [
        nextPair.latitude.topicData,
        nextPair.longitude.topicData,
      ];

      // Disallow editing data to create a duplicate path
      if (isDuplicatePath(nextTopicData)) {
        setError("Path already exists");
        return;
      }

      dispatch(
        actions.setDataSource({
          topicData: nextTopicData,
          panelId,
          segmentId: path.id,
        }),
      );
      setError(undefined);
    },
    [dispatch, isDuplicatePath, panelId, path.id],
  );

  const isLatitude = React.useCallback(
    (messagePathNode: MessagePathNode) => {
      if (pair.longitude) {
        return (
          messagePathNode.topic.id === pair.longitude.topicData.topic.id &&
          getCorrespondingGeoType(pair.longitude.canonicalDataType).includes(
            messagePathNode.data.canonical_data_type,
          )
        );
      }
      return isLatitudeType(messagePathNode.data.canonical_data_type);
    },
    [pair.longitude],
  );

  const isLongitude = React.useCallback(
    (messagePathNode: MessagePathNode) => {
      if (pair.latitude) {
        return (
          messagePathNode.topic.id === pair.latitude.topicData.topic.id &&
          getCorrespondingGeoType(pair.latitude.canonicalDataType).includes(
            messagePathNode.data.canonical_data_type,
          )
        );
      }
      return isLongitudeType(messagePathNode.data.canonical_data_type);
    },
    [pair.latitude],
  );

  const geoSpecificFirst = React.useCallback(
    (a: MessagePathNode, b: MessagePathNode) => {
      const aIsGeo = isGeoSpecificType(a.data.canonical_data_type);
      const bIsGeo = isGeoSpecificType(b.data.canonical_data_type);
      if (aIsGeo === bIsGeo) {
        return 0;
      }
      return aIsGeo ? -1 : 1;
    },
    [],
  );

  const onLatitudeChange = React.useCallback(
    (latitudeMessagePathNode: MessagePathNode) => {
      const latTopicData: TopicData = latitudeMessagePathNode.toTopicData();
      const nextLatitude = {
        canonicalDataType: latitudeMessagePathNode.data.canonical_data_type,
        topicData: latTopicData,
      };

      const nextPair: GeoPair = {
        latitude: nextLatitude,
        longitude: pair.longitude,
      };

      setPair(nextPair);
      commitPair(nextPair);
    },
    [commitPair, pair.longitude],
  );

  const onLongitudeChange = React.useCallback(
    (longitudeMessagePathNode: MessagePathNode) => {
      const lonTopicData: TopicData = longitudeMessagePathNode.toTopicData();
      const nextLongitude = {
        canonicalDataType: longitudeMessagePathNode.data.canonical_data_type,
        topicData: lonTopicData,
      };

      const nextPair: GeoPair = {
        latitude: pair.latitude,
        longitude: nextLongitude,
      };

      setPair(nextPair);
      commitPair(nextPair);
    },
    [commitPair, pair.latitude],
  );

  const onLatitudeClear = React.useCallback(() => {
    setPair((oldPair) => ({ latitude: null, longitude: oldPair.longitude }));
  }, []);

  const onLongitudeClear = React.useCallback(() => {
    setPair((oldPair) => ({ latitude: oldPair.latitude, longitude: null }));
  }, []);

  // Unit selectors are only shown for generic Number fields — geo-specific types
  // (LatDegFloat, LatDegInt, LonDegFloat, LonDegInt) encode the unit implicitly.
  const needsLatUnit =
    pair.latitude !== null &&
    !isGeoSpecificType(pair.latitude.canonicalDataType);
  const needsLonUnit =
    pair.longitude !== null &&
    !isGeoSpecificType(pair.longitude.canonicalDataType);

  const onLatUnitChange = React.useCallback(
    (event: SelectChangeEvent<CoordinateUnit | "">) => {
      const value = event.target.value;
      const latUnit = value !== "" ? (value as CoordinateUnit) : undefined;
      dispatch(
        actions.setPathCoordinateUnits(panelId, path.id, {
          lat: latUnit,
          lon: path.coordinateUnits?.lon,
        }),
      );
    },
    [dispatch, panelId, path.id, path.coordinateUnits?.lon],
  );

  const onLonUnitChange = React.useCallback(
    (event: SelectChangeEvent<CoordinateUnit | "">) => {
      const value = event.target.value;
      const lonUnit = value !== "" ? (value as CoordinateUnit) : undefined;
      dispatch(
        actions.setPathCoordinateUnits(panelId, path.id, {
          lat: path.coordinateUnits?.lat,
          lon: lonUnit,
        }),
      );
    },
    [dispatch, panelId, path.id, path.coordinateUnits?.lat],
  );

  return (
    <div className={styles.dataSources}>
      {error && <Alert severity="error">{error}</Alert>}
      <div>
        <RobotoTypography component={"label"} variant="subtitle2">
          Latitude
        </RobotoTypography>
        <DataSourceSelector
          initialFile={latitude.fileId}
          initialPath={latitude.path}
          onChange={onLatitudeChange}
          onClear={onLatitudeClear}
          optionFilter={isLatitude}
          optionSort={geoSpecificFirst}
        />
        {needsLatUnit && (
          <CoordinateUnitSelect
            coordinateType="latitude"
            value={path.coordinateUnits?.lat ?? ""}
            onChange={onLatUnitChange}
          />
        )}
      </div>
      <div>
        <RobotoTypography component={"label"} variant="subtitle2">
          Longitude
        </RobotoTypography>
        <DataSourceSelector
          initialFile={longitude.fileId}
          initialPath={longitude.path}
          onChange={onLongitudeChange}
          onClear={onLongitudeClear}
          optionFilter={isLongitude}
          optionSort={geoSpecificFirst}
        />
        {needsLonUnit && (
          <CoordinateUnitSelect
            coordinateType="longitude"
            value={path.coordinateUnits?.lon ?? ""}
            onChange={onLonUnitChange}
          />
        )}
      </div>
    </div>
  );
}
